import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { DraggableInput } from "./Draggable";

export const FormLayout = ({
  fields = [],
  onSubmit,
  moveField,
  removeField,
}) => {
  const [formData, setFormData] = useState({});
  useEffect(() => {
    const initial = fields.reduce((acc, f) => {
      acc[f.name] = f.defaultValue ?? "";
      return acc;
    }, {});
    setFormData(initial);
  }, [fields]);
  const handleChange = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  return (
    <Card elevation={3} sx={{ borderRadius: 2, mt: 2 }}>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2}>
            {fields.length > 0 ? (
              fields.map((field, idx) => {
                const { name, label, type, options, required } = field;
                let component = null;
                if (type === "text" || type === "number") {
                  component = (
                    <TextField
                      id={name}
                      label={label}
                      type={type}
                      required={required}
                      value={formData[name]}
                      onChange={(e) => handleChange(name, e.target.value)}
                      fullWidth
                    />
                  );
                } else {
                  switch (type) {
                    case "textarea":
                      component = (
                        <TextField
                          id={name}
                          label={label}
                          value={formData[name]}
                          onChange={(e) => handleChange(name, e.target.value)}
                          fullWidth
                          multiline
                          rows={4}
                          required={required}
                        />
                      );
                      break;
                    case "select":
                      component = (
                        <FormControl fullWidth required={required}>
                          <InputLabel id={`${name}-label`}>{label}</InputLabel>
                          <Select
                            labelId={`${name}-label`}
                            id={name}
                            label={label}
                            value={formData[name]}
                            onChange={(e) => handleChange(name, e.target.value)}
                          >
                            {options.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                      break;
                    case "checkbox":
                      component = (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formData[name])}
                              onChange={(e) =>
                                handleChange(name, e.target.checked)
                              }
                            />
                          }
                          label={label}
                        />
                      );
                      break;
                    case "radio":
                      component = (
                        <FormControl component="fieldset" required={required}>
                          <FormLabel component="legend">{label}</FormLabel>
                          <RadioGroup
                            value={formData[name]}
                            onChange={(e) => handleChange(name, e.target.value)}
                          >
                            {options.map((opt) => (
                              <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={<Radio />}
                                label={opt.label}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      );
                      break;
                    default:
                      component = null;
                  }
                }
                return component ? (
                  <DraggableInput
                    key={name}
                    index={idx}
                    moveField={moveField}
                    onRemove={() => removeField(idx)}
                  >
                    {component}
                  </DraggableInput>
                ) : null;
              })
            ) : (
              <Box
                sx={{
                  p: 4,
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                Drop a field from the palette here to start building your form.
              </Box>
            )}
            {fields.length > 0 && (
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            )}
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};
